/*
 * nomad-bundle-add.js
 *
 * Capture-phase click interceptor on the product form's submit button.
 * When the user has selected a chain or enabled engraving, builds an
 * items[] array containing the pendant + optional engraving variant +
 * optional chain variant and POSTs them in a single /cart/add.js call.
 * Falls through to Dawn's default single-item submit when no add-ons
 * are active.
 *
 * NOTE ON WHERE THE ENGRAVING FIELDS COME FROM
 *
 * They are read straight off the engraving panel, not out of
 * `new FormData(form)`. FormData only collects controls *inside* the
 * <form> element, and the engraving panel is rendered well above
 * <product-form> in main-product.liquid with no `form=""` attribute tying
 * it back. So every `properties[_engraving_*]` field was invisible to
 * FormData, and the only engraving detail that ever reached Shopify was
 * `_for_pendant`, which this file sets by hand.
 *
 * Order #1011 is what that looked like in production: the customer paid
 * for an engraving, the Engraving Service line arrived carrying nothing
 * but the pendant's name, and the workshop had no text to engrave.
 *
 * The chain selector never had this bug because it already reads its
 * values from the DOM. Engraving now does the same.
 *
 * AND A SECOND COPY
 *
 * Because one path failing silently is exactly how #1011 happened, the
 * request is also sent to the atelier's own API once the cart add has
 * succeeded, with a reference stamped onto the cart so the order can be
 * matched back to it. That half is fire-and-forget: the sale is already
 * made by then, and a failure to record must never undo it.
 */

(function () {
  if (window.__nomadBundleAddInit) return;
  window.__nomadBundleAddInit = true;

  var ENGRAVING_API = 'https://www.nomadcraftatelier.com/api/engraving';

  function getChainState() {
    var wrap = document.querySelector('[data-nomad-chain-selector]');
    if (!wrap) return null;
    var vidInput = wrap.querySelector('[data-chain-prop="variant_id"]');
    var titleInput = wrap.querySelector('[data-chain-prop="title"]');
    var priceInput = wrap.querySelector('[data-chain-prop="price"]');
    return {
      wrap: wrap,
      variantId: vidInput && vidInput.value,
      reset: function () {
        if (vidInput) vidInput.value = '';
        if (titleInput) titleInput.value = '';
        if (priceInput) priceInput.value = '';
        var cards = wrap.querySelectorAll('.nomad-chain-card');
        cards.forEach(function (c) { c.classList.remove('is-selected'); });
        var noChain = wrap.querySelector('[data-no-chain]');
        if (noChain) noChain.classList.add('is-selected');
      },
    };
  }

  function getEngravingState() {
    var wrap = document.querySelector('[data-nomad-engraving]');
    if (!wrap) return null;
    var toggle = wrap.querySelector('[data-engraving-toggle]');
    return {
      wrap: wrap,
      toggle: toggle,
      enabled: !!(toggle && toggle.checked),
      variantId: wrap.getAttribute('data-engraving-variant-id'),

      /*
       * What the customer asked to have engraved, read from the panel's own
       * controls. Radios and checkboxes only count when selected, and blank
       * fields are dropped so an unused second line doesn't reach the
       * workshop as an empty instruction.
       */
      props: function () {
        var out = {};
        var fields = wrap.querySelectorAll(
          'input[name^="properties["], textarea[name^="properties["], select[name^="properties["]'
        );
        Array.prototype.forEach.call(fields, function (el) {
          var m = el.name.match(/^properties\[(.+)\]$/);
          if (!m) return;
          if ((el.type === 'radio' || el.type === 'checkbox') && !el.checked) return;
          var val = (el.value || '').trim();
          if (!val) return;
          out[m[1]] = val;
        });
        return out;
      },

      reset: function () {
        if (toggle && toggle.checked) {
          toggle.checked = false;
          toggle.dispatchEvent(new Event('change'));
        }
      },
    };
  }

  /** Reference shared by the stored row and the cart attribute. */
  function newEngravingRef() {
    return 'eng_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 10);
  }

  /**
   * Record the request and stamp the cart with its reference.
   *
   * Cart attributes arrive on the order as note_attributes, the same
   * mechanism already carrying the analytics id, and the only join key
   * available: Shopify's Order exposes no cart or checkout token.
   *
   * Never throws. Runs only after the cart add succeeded.
   */
  function recordEngraving(props, pendantTitle, pendantHandle) {
    try {
      if (!props || (!props['_engraving_text'] && !props['_engraving_text2'])) return;

      var ref = newEngravingRef();
      var attributes = {};
      attributes['_nc_engraving_ref'] = ref;

      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: attributes }),
      }).catch(function (e) {
        console.warn('[nomad engraving] cart stamp failed', e);
      });

      fetch(ENGRAVING_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // Write-only endpoint on another origin; no cookies needed or sent.
        credentials: 'omit',
        keepalive: true,
        body: JSON.stringify({
          ref: ref,
          pendantTitle: pendantTitle || null,
          pendantHandle: pendantHandle || null,
          text1: props['_engraving_text'] || null,
          text2: props['_engraving_text2'] || null,
          layout: props['_engraving_layout'] || null,
          font: props['_engraving_font'] || null,
          icon: props['_engraving_icon'] || null,
          source: 'theme',
        }),
      }).catch(function (e) {
        console.warn('[nomad engraving] record failed', e);
      });
    } catch (e) {
      console.warn('[nomad engraving] unexpected', e);
    }
  }

  function attach() {
    var productForm = document.querySelector('product-form');
    if (!productForm) {
      setTimeout(attach, 200);
      return;
    }
    var form = productForm.querySelector('form');
    var submitBtn = productForm.querySelector('[type="submit"]');
    if (!form || !submitBtn || submitBtn.dataset.nomadBundled) return;
    submitBtn.dataset.nomadBundled = '1';

    submitBtn.addEventListener('click', function (e) {
      var chain = getChainState();
      var engraving = getEngravingState();
      var hasChain = !!(chain && chain.variantId);
      var hasEngraving = !!(engraving && engraving.enabled && engraving.variantId);

      if (!hasChain && !hasEngraving) return; // Dawn handles plain pendant
      if (submitBtn.getAttribute('aria-disabled') === 'true') return;

      e.preventDefault();
      e.stopImmediatePropagation();

      var spinner = productForm.querySelector('.loading__spinner');
      submitBtn.setAttribute('aria-disabled', 'true');
      submitBtn.classList.add('loading');
      if (spinner) spinner.classList.remove('hidden');

      var fd = new FormData(form);
      var mainId = parseInt(fd.get('id'), 10);
      var qty = parseInt(fd.get('quantity') || '1', 10);

      // Properties that genuinely live inside the form belong to the pendant.
      // Anything engraving- or chain-related is handled by its own panel.
      var pendantProps = {};
      var engravingProps = {};
      var entries = Array.from(fd.entries());
      for (var i = 0; i < entries.length; i++) {
        var key = entries[i][0];
        var val = entries[i][1];
        var m = key.match(/^properties\[(.+)\]$/);
        if (!m || !val) continue;
        var propName = m[1];
        if (propName.indexOf('_chain_') === 0) continue;
        if (propName.indexOf('_engraving_') === 0) {
          engravingProps[propName] = val;
        } else {
          pendantProps[propName] = val;
        }
      }

      var titleEl = document.querySelector('.product__title h1') || document.querySelector('.product__title');
      var mainTitle = titleEl ? titleEl.innerText.trim() : 'Pendant';
      var mainHandle = (window.location.pathname.split('/products/')[1] || '').split('?')[0];

      var items = [{ id: mainId, quantity: qty, properties: pendantProps }];

      if (hasEngraving) {
        // The panel is the source of truth; the loop above finds nothing
        // while the fields sit outside the form, and would be right to win
        // if a future layout ever moves them inside it.
        var panelProps = engraving.props();
        Object.keys(panelProps).forEach(function (k) {
          engravingProps[k] = panelProps[k];
        });

        engravingProps['_for_pendant'] = mainTitle;
        items.push({
          id: parseInt(engraving.variantId, 10),
          quantity: qty,
          properties: engravingProps,
        });
      }

      if (hasChain) {
        items.push({
          id: parseInt(chain.variantId, 10),
          quantity: qty,
          properties: { '_bundled_with': mainTitle },
        });
      }

      var cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      var sections = [];
      if (cart && typeof cart.getSectionsToRender === 'function') {
        sections = cart.getSectionsToRender().map(function (s) { return s.id; });
      }

      var body = { items: items };
      if (sections.length) {
        body.sections = sections.join(',');
        body.sections_url = window.location.pathname;
      }

      if (cart && typeof cart.setActiveElement === 'function') {
        cart.setActiveElement(document.activeElement);
      }

      fetch((window.routes && window.routes.cart_add_url) || '/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(body),
      })
        .then(function (r) { return r.json(); })
        .then(function (response) {
          if (response.status) {
            if (window.publish && window.PUB_SUB_EVENTS) {
              window.publish(window.PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: mainId,
                errors: response.errors || response.description,
                message: response.message,
              });
            }
            console.error('[nomad bundle add]', response.message || response.description);
            return;
          }

          // The sale is made; the second copy is written from here on.
          if (hasEngraving) {
            recordEngraving(engravingProps, mainTitle, mainHandle);
          }

          if (!cart) {
            window.location = (window.routes && window.routes.cart_url) || '/cart';
            return;
          }
          if (window.publish && window.PUB_SUB_EVENTS) {
            window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: mainId,
              cartData: response,
            });
          }
          cart.renderContents(response);
          if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');

          if (chain) chain.reset();
          if (engraving) engraving.reset();
        })
        .catch(function (err) {
          console.error('[nomad bundle add]', err);
        })
        .finally(function () {
          submitBtn.classList.remove('loading');
          submitBtn.removeAttribute('aria-disabled');
          if (spinner) spinner.classList.add('hidden');
        });
    }, true); // capture phase
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', attach);
  } else {
    attach();
  }
})();
